import type { ReactNode } from 'react';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Tag.css';

export type TagSize = 'small' | 'medium';

export interface TagProps {
  children: ReactNode;
  size?: TagSize;
  /** Filled plum when on — a filter that is in play, not a primary action. */
  selected?: boolean;
  /** Show a check before the label while selected — a picked suggestion. */
  markSelected?: boolean;
  /** Turns the chip into a toggle. */
  onSelectedChange?: (selected: boolean) => void;
  /**
   * Shows a trailing close mark. The mark is its own button, so dismissing
   * never also toggles.
   */
  onDismiss?: () => void;
  /** Accessible name for the close mark. Defaults to "Remove". */
  dismissLabel?: string;
  disabled?: boolean;
  /** Parent id. Dismiss and the toggle action hang off it. */
  testId?: string;
  className?: string;
}

export function Tag({
  children,
  size = 'medium',
  selected = false,
  markSelected = false,
  onSelectedChange,
  onDismiss,
  dismissLabel = 'Remove',
  disabled = false,
  testId,
  className,
}: TagProps) {
  const classes = ['tag', `tag--${size}`];
  if (selected) {
    classes.push('tag--selected');
  }
  if (disabled) {
    classes.push('tag--disabled');
  }
  if (onSelectedChange || onDismiss) {
    classes.push('tag--interactive');
  }
  if (className) {
    classes.push(className);
  }

  const mark =
    selected && markSelected ? (
      <CheckIcon className="tag__check" aria-hidden="true" />
    ) : null;

  const label = (
    <Typography
      as="span"
      role="label/tag"
      size={size === 'small' ? 'sm' : undefined}
      color="inherit"
      className="tag__label"
    >
      {children}
    </Typography>
  );
  const dismiss = onDismiss ? (
    <button
      className="tag__dismiss"
      type="button"
      disabled={disabled}
      aria-label={dismissLabel}
      onClick={(event) => {
        event.stopPropagation();
        onDismiss();
      }}
      {...testIdAttr(testIdFor(testId, 'dismiss'))}
    >
      <CloseIcon />
    </button>
  ) : null;

  // A single button when the whole chip toggles; nested buttons are invalid, so
  // a selectable-and-dismissible chip wraps two sibling buttons instead.
  if (onSelectedChange && !onDismiss) {
    return (
      <button
        className={classes.join(' ')}
        type="button"
        disabled={disabled}
        aria-pressed={selected}
        onClick={() => onSelectedChange(!selected)}
        {...testIdAttr(testId)}
      >
        {mark}
        {label}
      </button>
    );
  }

  if (onSelectedChange && onDismiss) {
    return (
      <span className={classes.join(' ')} {...testIdAttr(testId)}>
        <button
          className="tag__action"
          type="button"
          disabled={disabled}
          aria-pressed={selected}
          onClick={() => onSelectedChange(!selected)}
          {...testIdAttr(testIdFor(testId, 'action'))}
        >
          {mark}
          {label}
        </button>
        {dismiss}
      </span>
    );
  }

  return (
    <span className={classes.join(' ')} {...testIdAttr(testId)}>
      {mark}
      {label}
      {dismiss}
    </span>
  );
}
